/**
 * Artifact gallery surface for the single-slot workspace.
 *
 * Shows the ArtifactsGallery embedded beside the chat rail. The host decides
 * whether the pane is locked to workflowId's artifacts -- when it is, the
 * workflow dropdown is hidden so the user cannot browse away. Direct URL
 * syncing is disabled so the gallery does not fight the host's params;
 * persisted filters are controlled by the host. The close control rides in
 * the gallery's own filter row rather than floating over the grid.
 */

import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ArtifactsGallery } from '@/components/artifacts/artifacts-gallery';
import type { ResourceScope } from '@/components/scope-tabs';
import type { ArtifactListItem, ArtifactSortKey } from '@/types/artifacts';

export interface ArtifactGallerySurfaceProps {
  /** Pre-filter the gallery to artifacts from this workflow. */
  workflowId?: string;
  /**
   * Hides the workflow filter dropdown and keeps the gallery on workflowId.
   * Set by the host, not derived here -- see the SurfaceDescriptor field this
   * mirrors for why.
   */
  lockWorkflowFilter?: boolean;
  /**
   * Which My/Shared tab to show, when the host keeps that in its own URL.
   * Pass alongside onScopeChange; without the pair the gallery owns the
   * choice in state a reload discards.
   */
  scope?: ResourceScope;
  /** Sort order selected by the user. */
  sort?: ArtifactSortKey;
  /** Called when the user switches My/Shared, for a host that owns the tab. */
  onScopeChange?: (scope: ResourceScope) => void;
  /** Called when the sort order changes. */
  onSortChange?: (sort: ArtifactSortKey) => void;
  /** Called when the workflow filter changes. */
  onWorkflowFilterChange?: (workflowId: string | null) => void;
  /** Called when the user closes the gallery pane. */
  onClose?: () => void;
  /**
   * Called when the user activates "View Workflow" on a gallery group.
   * Without it ArtifactsGallery falls back to navigating to the standalone
   * workflow run route, leaving the host's surface pane behind.
   */
  onWorkflowNavigate?: (workflowId: string, runId: string | null) => void;
  /**
   * Called when the user clicks an artifact card inside the gallery. When
   * supplied the artifact opens as a layered surface in the pane instead of
   * navigating to the standalone detail route.
   */
  onArtifactOpen?: (artifact: ArtifactListItem) => void;
}

export function ArtifactGallerySurface({
  workflowId,
  lockWorkflowFilter = false,
  scope,
  sort,
  onScopeChange,
  onSortChange,
  onWorkflowFilterChange,
  onClose,
  onWorkflowNavigate,
  onArtifactOpen,
}: ArtifactGallerySurfaceProps) {
  return (
    <div className='flex h-full min-w-0 flex-1 flex-col overflow-auto bg-background'>
      <div className='p-6'>
        <ArtifactsGallery
          workflowId={workflowId}
          syncUrlParams={false}
          scope={scope}
          onScopeChange={onScopeChange}
          sort={sort}
          onSortChange={onSortChange}
          onWorkflowFilterChange={
            lockWorkflowFilter ? undefined : onWorkflowFilterChange
          }
          lockWorkflowFilter={lockWorkflowFilter}
          onWorkflowNavigate={onWorkflowNavigate}
          onArtifactOpen={onArtifactOpen}
          trailingAction={
            onClose && (
              <Button
                variant='ghost'
                size='icon'
                className='h-9 w-9'
                aria-label='Close artifact gallery'
                onClick={onClose}
              >
                <X className='size-4' />
              </Button>
            )
          }
        />
      </div>
    </div>
  );
}
